import React from "react";
import { type FormikProps } from "formik";
import { type EmailTemplateFormFields, type EmailTemplateFormProps } from "../types";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";
import { ValidatedForm } from "components/forms";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { bindActionCreators } from "redux";
import { campaignValidationSelector } from "../../state/selectors";
import { setInitialized, setIsValid } from "../../state/slices/campaignValidationSlice";
import * as validationActions from "../../state/thunks/campaignValidationThunk";
import { useFormikValidation } from "features/Library/Common/Hooks/useFormikValidationHook";
import { bindAction } from "interfaces/redux";
import WysiwygEditor from "components/WysiwygEditor/WysiwygEditor";
import { Grid } from "semantic-ui-react";
import styles from "./EmailTemplateForm.module.scss";

export type EmailTemplateFormPropsAll = FormikProps<EmailTemplateFormFields> &
  EmailTemplateFormProps &
  PropsFromRedux & { id: number };

const EmailTemplateForm = (props: EmailTemplateFormPropsAll) => {
  const validationStep = "emailTemplateValidation";
  const {
    values,
    isLoading,
    isLoaded,
    onBlur,
    disabled,
    errors,
    activeErrors,
    validateActions,
    isFormValid,
    resolved,
    touchedSnapshot,
    initialized,
    id,
  } = props;

  const { setActiveErrorsAction, checkForResolvedErrors } = validateActions;

  const formik = extractFormikProps<EmailTemplateFormFields, EmailTemplateFormPropsAll>(props);
  const nameof = nameofFactory<EmailTemplateFormFields>();

  useFormikValidation(
    { errors, resolved, initialized, activeErrors, isFormValid },
    {
      setInitializedAction: (isInitialized) => props.setInitialized({ validationStep, isInitialized }),
      setActiveErrors: (errors) => setActiveErrorsAction(validationStep, errors),
      setIsValidAction: (isValid) => props.setIsValid({ validationStep, isValid }),
      checkForResolvedErrors: (errors) => checkForResolvedErrors(validationStep, errors),
    },
  );

  return (
    <ValidatedForm
      dirty={props.dirty}
      disablePreventTransitionPrompt
      isLoading={isLoading || !isLoaded}
      withoutFormik
      fullsize
    >
      <div className={styles.inputs}>
        <Grid>
          <Grid.Row>
            <Grid.Column computer={6} tablet={12} mobile={16}>
              <ValidatedForm.InputField
                {...formik}
                label="Sender Name"
                value={values.senderName}
                propertyName={nameof("senderName")}
                placeholder="Enter Sender Name"
                touched={!resolved ? touchedSnapshot : formik.touched}
                onBlur={onBlur}
                markAsRequired
                disabled={disabled}
              />
              <ValidatedForm.InputField
                {...formik}
                label="Sender Email"
                value={values.senderEmail}
                propertyName={nameof("senderEmail")}
                placeholder="Enter Sender Email"
                touched={!resolved ? touchedSnapshot : formik.touched}
                onBlur={onBlur}
                markAsRequired
                disabled={disabled}
              />
              <ValidatedForm.InputField
                {...formik}
                label="Subject Line"
                value={values.subject}
                propertyName={nameof("subject")}
                placeholder="Enter Subject"
                touched={!resolved ? touchedSnapshot : formik.touched}
                onBlur={onBlur}
                markAsRequired
                disabled={disabled}
              />
              <ValidatedForm.InputField
                {...formik}
                label="Preview Text"
                value={values.previewText}
                propertyName={nameof("previewText")}
                placeholder="Enter Preview Text"
                touched={!resolved ? touchedSnapshot : formik.touched}
                onBlur={onBlur}
                disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      <WysiwygEditor
        {...formik}
        label={"Body"}
        markAsRequired
        value={values.html}
        defaultValue={formik.initialValues.html}
        onBlur={onBlur}
        campaignId={id}
        entityName={"email-template"}
        disabled={disabled}
        placeholder={"Enter Body"}
        propertyName={nameof("html")}
        touched={!resolved ? touchedSnapshot : formik.touched}
      />
    </ValidatedForm>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    ...campaignValidationSelector(state).emailTemplateValidation,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validateActions: bindActionCreators(validationActions, dispatch),
  setIsValid: bindAction(setIsValid, dispatch),
  setInitialized: bindAction(setInitialized, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(EmailTemplateForm);
export default ConnectedComponent;
